import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { buildEmbedUrl, isNativeSource, parseVideoSource, type VideoSource } from './source.js'
import {
  isFromSource,
  mute,
  parseMessage,
  pause,
  play,
  seekTo,
  subscribeUntilReady,
  unmute,
} from './embedBridge.js'
import styles from './VideoPlayer.module.css'

/**
 * Jediná komponenta pro přehrávání videa v celé aplikaci.
 * O poskytovateli nerozhoduje — dostane URL a poradí si.
 *
 * Řeší tři věci, na kterých původní aplikace zamrzávala:
 *  1. autoplay se zvukem prohlížeč zablokuje → start je vždy ztlumený
 *     a nabídne se tlačítko pro zapnutí zvuku
 *  2. nedostupné video → `onError`, nikdy nekonečná bílá plocha
 *  3. video, které se vůbec nerozjede → hlídací časovač nabídne přeskočení
 */

/**
 * Po kolika sekundách bez rozehrání nabídnout přeskočení.
 *
 * E2E testy si hodnotu smějí zkrátit — jinak by u každého kroku s videem
 * čekaly naprázdno 15 sekund. V produkci se proměnná nikdy nenastavuje.
 */
const DEFAULT_STALL_TIMEOUT_MS = 15_000

/** Po jak dlouhém tichu usoudit, že prohlížeč zablokoval zvuk. */
const SOUND_FALLBACK_MS = 2_500

/**
 * Jak dlouho po `playVideo()` schovávat obraz.
 *
 * YouTube na příkaz `playVideo()` odpoví vlastní velkou ikonou uprostřed
 * videa (krátká animace, cca půl vteřiny) — a to i s `controls=0`. Jde
 * o vestavěnou reakci přehrávače, ne o ovládací prvek, takže ji nejde
 * vypnout parametrem URL. Řešení je stejné jako u pruhů nahoře a dole:
 * na tu chvíli se video schová za neprůhlednou vrstvu.
 */
const PLAY_FLASH_COVER_MS = 900

function stallTimeout(): number {
  const override = (globalThis as { __SAFETUTOR_STALL_MS__?: number }).__SAFETUTOR_STALL_MS__
  return typeof override === 'number' && override > 0 ? override : DEFAULT_STALL_TIMEOUT_MS
}

/**
 * Přál si uživatel zvuk výslovně (klikl na „Zapnout zvuk“)?
 *
 * Musí to přežít výměnu videa: kurz dává každému videu vlastní `key`, takže
 * se komponenta pokaždé odmontuje a stav uvnitř se ztratí. Bez tohohle
 * příznaku by uživatel v Safari zapínal zvuk u každého dalšího videa znovu —
 * prohlížeč totiž povolí automatické přehrání se zvukem jen chvíli po
 * uživatelském gestu, takže videa navazující na jiné video (bez otázky mezi
 * nimi) se ztlumí, i když si uživatel zvuk už jednou vyžádal.
 */
let soundUnlockedByUser = false

export interface VideoPlayerProps {
  url: string
  controls: boolean
  onEnded: () => void
  onError: (message: string) => void
  onSkip: () => void
  /** Pozastaví přehrávání — svázané s pauzou kurzu. */
  paused?: boolean
  /**
   * Přepnutí pauzy z pruhu ovládání videa.
   *
   * Když se předá, patří pauza do spodního pruhu k časové ose a volající
   * ji nesmí zároveň nabízet jinde — dvě tlačítka pro jednu věc jsou přesně
   * to, co uživatele mátlo.
   */
  onTogglePause?: () => void
  /** Popis pro čtečky obrazovky. */
  label?: string
  /**
   * Zkrácené čekání, než se nabídne přeskočení.
   *
   * Kurz ho posílá, když už uživatel musel videa přeskakovat opakovaně —
   * typicky když firemní síť blokuje YouTube a nerozjede se ani jedno.
   */
  stallAfterMs?: number
}

export function VideoPlayer({
  url,
  controls,
  onEnded,
  onError,
  onSkip,
  paused = false,
  onTogglePause,
  label,
  stallAfterMs,
}: VideoPlayerProps): React.ReactElement {
  // Bez memoizace vzniká nový objekt při každém renderu a posluchač zpráv
  // se pořád odpojuje a připojuje — zprávy z přehrávače se pak ztrácejí.
  const source = useMemo(() => parseVideoSource(url), [url])

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const [started, setStarted] = useState(false)
  /** Přehrávač odpověděl na handshake — teprve pak má smysl měřit, jestli hraje. */
  const [ready, setReady] = useState(false)
  const [muted, setMuted] = useState(false)
  const [stalled, setStalled] = useState(false)
  const [progress, setProgress] = useState({ current: 0, duration: 0 })
  const [playFlash, setPlayFlash] = useState(false)
  const playFlashTimer = useRef<number | null>(null)

  /** Zavolá `playVideo()` a na chvíli zakryje obraz, než odezní vlastní ikona přehrávače. */
  const playAndCover = useCallback((win: Window, src: VideoSource) => {
    play(win, src)
    setPlayFlash(true)
    if (playFlashTimer.current !== null) window.clearTimeout(playFlashTimer.current)
    playFlashTimer.current = window.setTimeout(() => setPlayFlash(false), PLAY_FLASH_COVER_MS)
  }, [])

  useEffect(
    () => () => {
      if (playFlashTimer.current !== null) window.clearTimeout(playFlashTimer.current)
    },
    [],
  )

  /**
   * Adresa iframu se sestaví JEDNOU na video a už se nemění.
   *
   * Dřív v ní bylo `mute`, takže zapnutí zvuku přepsalo `src`, iframe se
   * znovu načetl a video začalo od začátku. Hlasitost i pauza se řídí
   * výhradně přes `postMessage`.
   *
   * Startuje se rovnou se zvukem: do kurzu se vždy vstupuje kliknutím
   * (SPUSTIT nebo přihlášení), takže prohlížeč přehrávání se zvukem povolí.
   * Kdyby ho přesto zablokoval, hlídač níže video ztlumí a nabídne tlačítko.
   */
  const embedSrc = useMemo(
    () =>
      source
        ? buildEmbedUrl(source, {
            muted: false,
            controls,
            ...(typeof window !== 'undefined' ? { origin: window.location.origin } : {}),
          })
        : '',
    [source, controls],
  )

  /* --- reset při změně videa ------------------------------------------- */
  useEffect(() => {
    setStarted(false)
    setReady(false)
    setStalled(false)
    setMuted(false)
    setProgress({ current: 0, duration: 0 })
  }, [url])

  /**
   * Pauza čtená z posluchače zpráv.
   *
   * Posluchač se registruje jednou; kdyby měl `paused` v závislostech,
   * odpojoval by se při každé pauze a přišel by o události přehrávače.
   */
  const pausedRef = useRef(paused)
  useEffect(() => {
    pausedRef.current = paused
  }, [paused])

  /* --- záloha, když prohlížeč nepustí zvuk ------------------------------ */
  useEffect(() => {
    /*
     * Odpočet začíná až od `ready`, ne od načtení komponenty.
     *
     * Dřív se měřilo od připojení iframu, jenže do těch 2,5 s se musí vejít
     * načtení iframu, spuštění přehrávače, handshake I první buffer. To se
     * běžně nestihne, takže se ztlumilo i video, které by se zvukem hrálo
     * bez problémů — a protože doba načtení kolísá, vypadalo to, že zvuk
     * vypadává „obden“. Od `ready` už měříme jen to, co nás zajímá:
     * přehrávač je připravený, dostal příkaz hrát, a přesto nehraje.
     */
    if (!ready || started || paused || !source || isNativeSource(source)) return undefined

    const timer = window.setTimeout(() => {
      const win = iframeRef.current?.contentWindow
      if (!win) return
      setMuted(true)
      mute(win, source)
      play(win, source)
    }, SOUND_FALLBACK_MS)

    return () => window.clearTimeout(timer)
  }, [url, ready, started, paused, source])

  /* --- hlídač zaseknutí ------------------------------------------------ */
  useEffect(() => {
    // Jakmile video běží, časovač se musí ZRUŠIT — jinak hláška naskočí
    // i nad rozjetým videem, protože ji už nemá co schovat zpátky.
    if (started) {
      setStalled(false)
      return undefined
    }

    // Během pauzy hlídač nedává smysl: video nestojí kvůli závadě, ale proto,
    // že si ji uživatel vyžádal. Bez téhle podmínky vyskočila nad pozastaveným
    // kurzem po 15 vteřinách hláška „Video čeká na spuštění".
    if (paused) return undefined

    const timer = window.setTimeout(
      () => setStalled(true),
      Math.min(stallAfterMs ?? Number.POSITIVE_INFINITY, stallTimeout()),
    )
    return () => window.clearTimeout(timer)
  }, [url, started, paused, stallAfterMs])

  /* --- iframe: příjem událostí ----------------------------------------- */
  useEffect(() => {
    if (!source || isNativeSource(source)) return undefined

    function onMessage(event: MessageEvent): void {
      if (!source || !isFromSource(event.origin, source)) return

      // Jedna zpráva může nést změnu stavu i posun v čase zároveň
      for (const parsed of parseMessage(source, event.data)) {
        switch (parsed.kind) {
          case 'ready': {
            /*
             * Handshake se opakuje až 12 sekund a přehrávač na něj pokaždé
             * odpoví. Bez téhle podmínky každá odpověď spustila přehrávání —
             * kurz pozastavený krátce po startu videa se sám rozjel a
             * uživateli to připadalo, že tlačítko pauzy nefunguje.
             */
            // Až teď má smysl hlídat, jestli se rozjede se zvukem.
            setReady(true)
            if (pausedRef.current) break
            const win = iframeRef.current?.contentWindow
            // I tady: ikona se mihne, až když video reálně naběhne, což
            // bývá o kus později, než zmizí načítací překryv.
            if (win) playAndCover(win, source)
            break
          }
          case 'playing': {
            setStarted(true)
            // Pojistka: kdyby se přehrávač rozjel navzdory pauze, hned ho zastavíme.
            if (pausedRef.current) {
              const win = iframeRef.current?.contentWindow
              if (win) pause(win, source)
            }
            /*
             * Uživatel si zvuk už někdy vyžádal, ale tohle video muselo
             * začít ztlumené (prohlížeč nepustil automatické přehrání se
             * zvukem). Teď, když video prokazatelně hraje, se zvuk vrátí sám —
             * jinak by na něj musel klikat u každého dalšího videa.
             */
            if (soundUnlockedByUser) {
              setMuted((wasMuted) => {
                if (!wasMuted) return wasMuted
                const win = iframeRef.current?.contentWindow
                if (win) unmute(win, source)
                return false
              })
            }
            break
          }
          case 'progress': {
            // Délka chodí jen v občasných úplných zprávách — jinou si držíme.
            const { currentTime, duration } = parsed
            setProgress((prev) => ({
              current: currentTime,
              duration: duration ?? prev.duration,
            }))
            /*
             * Postup v čase je nezvratný důkaz, že video už reálně hraje —
             * nezávislý na zprávě „playing", která při vytížené záložce
             * (typicky souběžné stahování obrázků) dorazí i o víc než
             * SOUND_FALLBACK_MS později. Bez téhle podmínky časovač níže
             * ztlumil video, které už hrálo se zvukem — uživatel to viděl
             * jako zvuk, který se sám vypnul.
             */
            if (currentTime > 0) setStarted(true)
            break
          }
          case 'paused':
            break
          case 'ended':
            onEnded()
            break
          case 'error':
            onError(parsed.message)
            break
        }
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [source, onEnded, onError, playAndCover])

  /* --- pauza svázaná s pauzou kurzu ------------------------------------- */
  useEffect(() => {
    if (!source) return

    if (isNativeSource(source)) {
      const el = videoRef.current
      if (!el) return
      if (paused) el.pause()
      else if (started) void el.play()
      return
    }

    const win = iframeRef.current?.contentWindow
    if (!win) return
    if (paused) pause(win, source)
    else if (started) playAndCover(win, source)
  }, [paused, source, started, playAndCover])

  /* --- handshake s iframem --------------------------------------------- */
  const stopHandshake = useRef<(() => void) | null>(null)

  const endHandshake = useCallback(() => {
    stopHandshake.current?.()
    stopHandshake.current = null
  }, [])

  const handleIframeLoad = useCallback(() => {
    if (!source || isNativeSource(source)) return
    endHandshake()
    stopHandshake.current = subscribeUntilReady(() => iframeRef.current?.contentWindow, source)
  }, [source, endHandshake])

  // Jakmile přehrávač odpoví, opakování už není potřeba
  useEffect(() => {
    if (started) endHandshake()
  }, [started, endHandshake])

  useEffect(() => endHandshake, [url, endHandshake])

  /**
   * Ruční spuštění, když prohlížeč autoplay nepustil.
   * Kliknutí je uživatelské gesto, takže projde i se zvukem.
   */
  const startManually = useCallback(() => {
    setStalled(false)
    setMuted(false)
    // Gesto uživatele — zapamatovat pro další videa v kurzu.
    soundUnlockedByUser = true

    if (!source) return
    if (isNativeSource(source)) {
      const el = videoRef.current
      if (el) {
        el.muted = false
        void el.play()
      }
      return
    }

    const win = iframeRef.current?.contentWindow
    if (win) {
      unmute(win, source)
      playAndCover(win, source)
    }
  }, [source, playAndCover])

  /* --- přetáčení z vlastní časové osy ----------------------------------- */
  const handleSeek = useCallback(
    (seconds: number) => {
      if (!source) return

      if (isNativeSource(source)) {
        if (videoRef.current) videoRef.current.currentTime = seconds
      } else {
        const win = iframeRef.current?.contentWindow
        if (win) seekTo(win, source, seconds)
      }

      // Ukazatel posunout hned, ať osa nereaguje se zpožděním
      setProgress((p) => ({ ...p, current: seconds }))
    },
    [source],
  )

  /* --- zapnutí zvuku uživatelským gestem -------------------------------- */
  const enableSound = useCallback(() => {
    setMuted(false)
    // Gesto uživatele — zapamatovat pro další videa v kurzu.
    soundUnlockedByUser = true
    if (source && !isNativeSource(source)) {
      const win = iframeRef.current?.contentWindow
      if (win) {
        unmute(win, source)
        playAndCover(win, source)
      }
    } else if (videoRef.current) {
      videoRef.current.muted = false
      void videoRef.current.play()
    }
  }, [source, playAndCover])

  if (!source) {
    return (
      <div className={styles.wrapper}>
        <PlayerNotice
          title="Video se nepodařilo načíst"
          detail="Adresa videa nemá rozpoznatelný formát."
          actionLabel="Pokračovat"
          onAction={onSkip}
        />
      </div>
    )
  }

  return (
    <div className={styles.wrapper} data-testid="video-stage">
      {isNativeSource(source) ? (
        <video
          ref={videoRef}
          className={styles.nativeFrame}
          src={buildEmbedUrl(source, { muted, controls })}
          autoPlay
          muted={muted}
          playsInline
          controls={controls}
          onPlaying={() => setStarted(true)}
          onEnded={onEnded}
          onError={() => onError('Video se nepodařilo přehrát')}
          aria-label={label ?? 'Video kurzu'}
        />
      ) : (
        <div className={styles.frameClip}>
          <iframe
            ref={iframeRef}
            className={styles.frame}
            src={embedSrc}
            onLoad={handleIframeLoad}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            title={label ?? 'Video kurzu'}
          />
          {/* Nepustí ukazatel k iframu — jinak poskytovatel ukáže chrome i při hoveru */}
          <div className={styles.shield} aria-hidden="true" />
          {/*
            Krytka vlastní ikony přehrávače. YouTube na `playVideo()` odpoví
            velkou ikonou uprostřed videa bez ohledu na `controls=0` — jde
            o reakci na příkaz, ne o ovládací prvek, takže se nedá vypnout
            parametrem URL. Na tu chvíli video schová.
          */}
          {playFlash && <div className={styles.playFlashCover} aria-hidden="true" />}
        </div>
      )}

      {/*
        Během pauzy se nenačítá — čeká se na uživatele. Točící se kolečko
        s hláškou „Načítám video…" si odporovalo s odznakem „Kurz je
        pozastaven" hned vedle.
      */}
      {!started && !stalled && !paused && (
        <div className={styles.overlay} role="status" aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          <span>Načítám video…</span>
        </div>
      )}

      {stalled && (
        <PlayerNotice
          title="Video čeká na spuštění"
          detail="Prohlížeč nespustil video sám. Spusťte ho tlačítkem, nebo pokračujte dál."
          actionLabel="▶ Přehrát"
          onAction={startManually}
          secondaryLabel="Pokračovat bez videa"
          onSecondary={onSkip}
        />
      )}

      {/*
        JEDINÝ pruh ovládání videa, u spodní hrany — pauza, časová osa, zvuk.
        Pauza sem patří proto, že jinde ležela uprostřed pravého okraje,
        tedy přímo v obraze videa. Ovládání kurzu je nad ní jen nabídka.
      */}
      <PlayerBar
        current={progress.current}
        duration={progress.duration}
        muted={muted}
        paused={paused}
        onSeek={handleSeek}
        onEnableSound={enableSound}
        onTogglePause={onTogglePause}
      />
    </div>
  )
}

/** `M:SS` — u kurzovních videí (do minuty) není potřeba nic delšího. */
function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface PlayerBarProps {
  current: number
  duration: number
  muted: boolean
  paused: boolean
  onSeek: (seconds: number) => void
  onEnableSound: () => void
  onTogglePause: (() => void) | undefined
}

/**
 * Ovládání videa — jeden decentní pruh u spodní hrany.
 *
 * Ovládání poskytovatele je vypnuté, takže tohle je jediná cesta, jak se ve
 * videu posunout nebo si zapnout zvuk. V klidu je zpola průhledný a při
 * najetí či zaostření zesílí, aby nepřekážel obrazu.
 *
 * Osa je nativní `range`, ne vlastní kreslení — dostaneme tím zdarma
 * ovládání klávesnicí, dotykem i podporu čteček obrazovky.
 */
function PlayerBar({
  current,
  duration,
  muted,
  paused,
  onSeek,
  onEnableSound,
  onTogglePause,
}: PlayerBarProps): React.ReactElement | null {
  const percent = duration > 0 ? Math.min(100, (current / duration) * 100) : 0

  // Prázdný pruh by byl jen tmavá pilulka bez obsahu
  if (!onTogglePause && duration === 0 && !muted) return null

  return (
    <div className={styles.playerBar} data-testid="player-bar">
      {onTogglePause && (
        <button
          type="button"
          className={styles.barButton}
          onClick={onTogglePause}
          aria-label={paused ? 'Pokračovat' : 'Pozastavit'}
          title={paused ? 'Pokračovat' : 'Pozastavit'}
        >
          {paused ? <IconPlay /> : <IconPause />}
        </button>
      )}

      {duration > 0 && (
        <>
          <span className={styles.time}>{formatTime(current)}</span>

          <input
            className={styles.scrubber}
            type="range"
            min={0}
            max={Math.floor(duration)}
            step={1}
            value={Math.floor(current)}
            onChange={(e) => onSeek(Number(e.target.value))}
            aria-label="Posun ve videu"
            aria-valuetext={`${formatTime(current)} z ${formatTime(duration)}`}
            style={{ ['--played' as string]: `${percent}%` }}
          />

          <span className={styles.time}>{formatTime(duration)}</span>
        </>
      )}

      {/* Jen když prohlížeč nepustil zvuk sám — jinak start rovnou zní. */}
      {muted && (
        <button type="button" className={styles.soundButton} onClick={onEnableSound}>
          🔇 <span className={styles.soundLabel}>Zapnout zvuk</span>
        </button>
      )}
    </div>
  )
}

/* Vlastní ikony, ne knihovna — tvarem odpovídají těm v nabídce kurzu. */

function IconPause(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  )
}

function IconPlay(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" />
    </svg>
  )
}

interface PlayerNoticeProps {
  title: string
  detail: string
  actionLabel: string
  onAction: () => void
  secondaryLabel?: string
  onSecondary?: (() => void) | undefined
}

function PlayerNotice({
  title,
  detail,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
}: PlayerNoticeProps): React.ReactElement {
  return (
    <div className={styles.overlay} role="alert">
      <strong>{title}</strong>
      <p className={styles.detail}>{detail}</p>
      <div className={styles.actions}>
        <button type="button" className={styles.action} onClick={onAction}>
          {actionLabel}
        </button>
        {secondaryLabel && onSecondary && (
          <button
            type="button"
            className={`${styles.action} ${styles.secondary}`}
            onClick={onSecondary}
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </div>
  )
}

export type { VideoSource }
